import { useTranslation } from 'react-i18next';

import { useConfirmPopup } from 'components/popups/useConfirmPopup';
import { useLoginPopup } from 'features/account';
import { useBecomeFundraiserPopup } from 'features/my-profile/become-fundraiser/form/useBecomeFundraiserPopup';
import { useAlertPopup } from 'hooks/useAlertPopup';
import { usePopup } from 'hooks/usePopup';
import { useAuth } from 'providers/AuthProvider';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { useNotification } from 'providers/NotificationProvider';
import { CampaignDto } from 'services/campaign/campaignService.dto';
import { CurrentUserDto } from 'services/user/userService.dto';
import {
  CampaignFormDraft,
  clearCampaignFormDraft,
  getCampaignFormDraft,
  isCampaignFormDraftMeaningful,
} from 'utils/formDraftStorage';

import AddCampaignForm from './AddCampaignForm';
import EditCampaignForm from './EditCampaignForm';

export const useCampaignPopup = () => {
  const { t } = useTranslation('addCampaign');
  const { logged } = useAuth();
  const { currentUser, waitForCurrentUser } = useCurrentUser();
  const { showLoginPopup } = useLoginPopup();
  const { showPopup, hidePopup } = usePopup();
  const { showConfirmPopup } = useConfirmPopup();
  const { showBecomeFundraiserPopup } = useBecomeFundraiserPopup();
  const { showNotification } = useNotification();
  const { showAlertPopup } = useAlertPopup();

  const isLoading = logged && !currentUser;

  const handleCloseAdd = () => hidePopup();
  const handleCloseEdit = () => showConfirmPopup({ msg: t('quit-form-msg'), onYes: hidePopup });

  const checkUserAndOpenPopup = (openFormPopup: (user: CurrentUserDto) => void) => {
    if (!logged) showLoginPopup({ onLogged: () => verifyUserDataFetchedAndOpenPopup(openFormPopup) });
    else verifyUserDataFetchedAndOpenPopup(openFormPopup);
  };

  const verifyUserDataFetchedAndOpenPopup = async (openFormPopup: (user: CurrentUserDto) => void) => {
    const user = await waitForCurrentUser();
    if (!!user) {
      verifyUserCanCreateCampaignAndOpenPopup(user, openFormPopup);
    } else {
      showNotification({ title: t('common:global-error.unknown-error-message'), type: 'error' });
    }
  };

  const verifyUserCanCreateCampaignAndOpenPopup = (
    user: CurrentUserDto,
    openFormPopup: (user: CurrentUserDto) => void
  ) => {
    if (user.ableToCreateCampaigns) openFormPopup(user);
    else if (!user.ableToBecomeFundraiser) {
      showNotification({ title: t('myProfile:become-fundraiser.unable-to-become-fundraiser'), type: 'error' });
    } else showBecomeFundraiserPopup(user);
  };

  const openAddCampaignForm = (draft?: CampaignFormDraft) => {
    showPopup(<AddCampaignForm draft={draft} />, { onClose: handleCloseAdd, paddingVariant: 'FORM' });
  };

  const showAddCampaignPopup = () => {
    checkUserAndOpenPopup(user => {
      const draft = getCampaignFormDraft(user.id);

      if (isCampaignFormDraftMeaningful(draft)) {
        showAlertPopup({
          header: t('resume-draft.title'),
          description: t('resume-draft.message'),
          firstButton: {
            label: t('resume-draft.continue-btn'),
            onClick: () => openAddCampaignForm(draft),
          },
          secondButton: {
            label: t('resume-draft.start-new-btn'),
            onClick: () => {
              clearCampaignFormDraft(user.id);
              openAddCampaignForm();
            },
          },
          testId: 'resume-campaign-draft-alert',
        });
        return;
      }

      openAddCampaignForm();
    });
  };

  const showEditCampaignPopup = (campaign: CampaignDto, onCampaignSaved?: () => void) => {
    const handleSaved = () => {
      hidePopup();
      onCampaignSaved && onCampaignSaved();
    };

    checkUserAndOpenPopup(() =>
      showPopup(<EditCampaignForm campaign={campaign} onCampaignSaved={handleSaved} />, {
        onClose: handleCloseEdit,
        paddingVariant: 'FORM',
      })
    );
  };

  return { showAddCampaignPopup, showEditCampaignPopup, isLoading };
};
